import React, { useState, useEffect } from 'react';
import axiosPost from '../../../helpers/axios-helper';

/**
 * Call-to-action buttons for one venue: a text and a link each, in order.
 * Links may be a web address, a phone number (becomes "tel:") or an e-mail
 * address (becomes "mailto:"); the server fixes up what was typed.
 */
const PRESETS = [
  { label: 'Reserve', hint: 'https://… booking page' },
  { label: 'Visit website', hint: 'https://…' },
  { label: 'Call', hint: '+599 9 …' },
  { label: 'Directions', hint: 'https://maps.google.com/…' },
];

const MAX = 6;

export default function VenueButtonsEditor({ itemType, itemId }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setStatus('');
    if (!itemId) { setRows([]); return undefined; }
    const controller = new AbortController();
    axiosPost('buttons/for-item', { 'Content-Type': 'application/json' }, { item_type: itemType, item_id: itemId },
      (resp) => { if (resp?.success) setRows((resp.data.buttons || []).map((b) => ({ label: b.label, url: b.url }))); },
      controller);
    return () => controller.abort();
  }, [itemType, itemId]);

  const update = (i, field, value) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  const remove = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) => setRows((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const add = (label = '') => setRows((prev) => (prev.length >= MAX ? prev : [...prev, { label, url: '' }]));

  const save = () => {
    setIsSaving(true);
    setStatus('');
    axiosPost('buttons/save', { 'Content-Type': 'application/json' }, { item_type: itemType, item_id: itemId, buttons: rows },
      (resp) => {
        setIsSaving(false);
        if (resp?.success) {
          setRows((resp.data.buttons || []).map((b) => ({ label: b.label, url: b.url })));
          setStatus('Saved.');
        } else {
          setStatus(resp?.response || 'Could not save the buttons.');
        }
      });
  };

  return (
    <div style={{ margin: '24px 0' }}>
      <h3>Call-to-action buttons</h3>
      {!itemId ? (
        <p style={{ color: '#666' }}>Save this item first, then add buttons.</p>
      ) : (
        <>
          <p style={{ color: '#666', marginTop: 0 }}>
            Shown at the top of the public page. The first button is highlighted.
            A link can be a web address, a phone number or an e-mail address.
          </p>

          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <input className="DashboardFormInput" style={{ flex: '1 1 160px' }} placeholder="Button text"
                     value={r.label} maxLength={60} onChange={(e) => update(i, 'label', e.target.value)} />
              <input className="DashboardFormInput" style={{ flex: '2 1 260px' }}
                     placeholder={PRESETS.find((p) => p.label === r.label)?.hint || 'Link, phone number or e-mail'}
                     value={r.url} onChange={(e) => update(i, 'url', e.target.value)} />
              <button type="button" className="DashboardButtonSecondary" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
              <button type="button" className="DashboardButtonSecondary" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down">↓</button>
              <button type="button" className="DashboardButtonSecondary" onClick={() => remove(i)}>Remove</button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
            {PRESETS.map((p) => (
              <button key={p.label} type="button" className="DashboardButtonSecondary"
                      onClick={() => add(p.label)} disabled={rows.length >= MAX}>+ {p.label}</button>
            ))}
            <button type="button" className="DashboardButtonSecondary" onClick={() => add('')} disabled={rows.length >= MAX}>+ Custom</button>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button type="button" className="DashboardButton" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save buttons'}
            </button>
            {status && <span style={{ color: status === 'Saved.' ? '#2e7d32' : '#c62828' }}>{status}</span>}
          </div>
        </>
      )}
    </div>
  );
}
